import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, FileText, Star, Mail, RefreshCw, Shield,
  LogOut, Eye, EyeOff, Settings, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import SecurityDashboard from '../components/SecurityDashboard';
import SearchInsightsDashboard from '../components/SearchInsightsDashboard';
import VenuePagesManager from '../components/VenuePagesManager';
import HomepageCMS from '../components/HomepageCMS';
import { EstimatesManager } from '../components/pricing/EstimatesManager';
import { MediaLibrary } from '../components/media/MediaLibrary';
import { AdminSidebar } from '../components/admin/AdminSidebar';
import { LeadsPanel } from '../components/admin/LeadsPanel';
import { UsersPanel, VisitReportsPanel, VerificationPanel, TopVenuesPanel } from '../components/admin/IntelligencePanels';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATA_VIEWS = new Set(['leads', 'users', 'visit-reports', 'verification', 'top-venues']);
const STATS_VIEWS = new Set(['leads', 'analytics', 'users', 'visit-reports', 'verification', 'top-venues']);

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-stone-200 p-5">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-stone-900">{value}</p>
        <p className="text-sm text-stone-500">{label}</p>
      </div>
    </div>
  </div>
);

const AdminPage = () => {
  const navigate = useNavigate();

  // Admin auth state
  const [adminToken, setAdminToken] = useState(localStorage.getItem('wvc_admin_token') || '');
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Settings modal
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ currentPassword: '', newUsername: '', newPassword: '' });

  // Dashboard state
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [view, setView] = useState('venues');

  useEffect(() => {
    if (adminToken) {
      verifyAdminSession();
    }
  }, []);

  const verifyAdminSession = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/verify?admin_token=${adminToken}`);
      if (response.data.valid) {
        setIsAdminLoggedIn(true);
        setAdminUsername(response.data.username);
        fetchDashboardData();
      }
    } catch (error) {
      localStorage.removeItem('wvc_admin_token');
      setAdminToken('');
      setIsAdminLoggedIn(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      toast.error('Please enter username and password');
      return;
    }

    setIsLoggingIn(true);
    try {
      const response = await axios.post(`${API_URL}/admin/login`, {
        username: loginForm.username,
        password: loginForm.password
      });

      const { session_token, username } = response.data;
      localStorage.setItem('wvc_admin_token', session_token);
      setAdminToken(session_token);
      setIsAdminLoggedIn(true);
      setAdminUsername(username);
      toast.success('Admin login successful');
      fetchDashboardDataWithToken(session_token);
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleAdminLogout = async () => {
    try {
      await axios.post(`${API_URL}/admin/logout?admin_token=${adminToken}`);
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('wvc_admin_token');
    setAdminToken('');
    setIsAdminLoggedIn(false);
    setDashboardData(null);
    toast.success('Logged out successfully');
  };

  const handleUpdateCredentials = async () => {
    if (!settingsForm.currentPassword) {
      toast.error('Current password is required');
      return;
    }

    try {
      await axios.put(`${API_URL}/admin/credentials?admin_token=${adminToken}`, {
        current_password: settingsForm.currentPassword,
        new_username: settingsForm.newUsername || undefined,
        new_password: settingsForm.newPassword || undefined
      });

      toast.success('Credentials updated. Please login again.');
      setShowSettingsModal(false);
      handleAdminLogout();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update credentials';
      toast.error(message);
    }
  };

  const fetchDashboardData = async () => {
    fetchDashboardDataWithToken(adminToken);
  };

  const fetchDashboardDataWithToken = async (token) => {
    setIsLoading(true);
    try {
      const [dashboardRes, qualificationRes] = await Promise.all([
        axios.get(`${API_URL}/admin/dashboard?admin_token=${token}`),
        axios.get(`${API_URL}/admin/qualification-leads?admin_token=${token}`)
      ]);

      const qualificationData = qualificationRes.data;

      setDashboardData({
        ...dashboardRes.data,
        qualification: qualificationData.leads || [],
        qualificationStats: qualificationData.stats || {}
      });
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  // Admin Login Form
  if (!isAdminLoggedIn) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white border border-stone-200 rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-900 rounded-full mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-serif text-2xl font-medium text-stone-900">Admin Panel</h1>
              <p className="text-sm text-stone-500 mt-2">Secure access to WVC administration</p>
            </div>

            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Username</label>
                <Input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="Enter admin username"
                  className="h-12"
                  data-testid="admin-username"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter admin password"
                    className="h-12 pr-10"
                    data-testid="admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-stone-900 hover:bg-stone-800"
                disabled={isLoggingIn}
                data-testid="admin-login-btn"
              >
                {isLoggingIn ? 'Logging in...' : 'Login to Admin Panel'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-stone-500 hover:text-stone-700"
              >
                ← Back to Homepage
              </button>
            </div>
          </div>

          <p className="text-xs text-stone-400 text-center mt-4">
            This is a secure admin area. All actions are logged.
          </p>
        </div>
      </div>
    );
  }

  const needsData = DATA_VIEWS.has(view);
  const dataLoading = needsData && !dashboardData;

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFCF8]/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-serif text-lg sm:text-xl font-medium text-stone-900 flex items-center gap-2 truncate">
                <Shield className="w-5 h-5 text-green-600 shrink-0" />
                <span className="truncate">Admin Dashboard</span>
              </h1>
              <p className="text-xs sm:text-sm text-stone-500 truncate">Logged in as: {adminUsername}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={fetchDashboardData} disabled={isLoading} className="px-2 sm:px-3" title="Refresh" data-testid="admin-refresh-btn">
              <RefreshCw className={`w-4 h-4 sm:mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowSettingsModal(true)} className="px-2 sm:px-3" title="Settings" data-testid="admin-settings-btn">
              <Settings className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Settings</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleAdminLogout} className="px-2 sm:px-3" title="Logout" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-8">
        <div className="lg:flex lg:gap-8 items-start">
          <AdminSidebar active={view} onSelect={setView} />

          <div className="flex-1 min-w-0" data-testid="admin-content-area">
            {STATS_VIEWS.has(view) && dashboardData && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <StatCard icon={Users} label="Total Users" value={dashboardData.summary.total_users} color="bg-blue-500" />
                <StatCard icon={Mail} label="Total Leads" value={dashboardData.summary.total_leads} color="bg-green-500" />
                <StatCard icon={FileText} label="Reports Generated" value={dashboardData.summary.total_reports} color="bg-purple-500" />
                <StatCard icon={Star} label="Visit Reports" value={dashboardData.summary.total_experiences} color="bg-amber-500" />
                <StatCard icon={CheckCircle} label="Verification Requests" value={dashboardData.summary.total_verification_requests} color="bg-red-500" />
              </div>
            )}

            {dataLoading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto"></div>
              </div>
            ) : (
              <>
                {view === 'venues' && <VenuePagesManager adminToken={adminToken} />}
                {view === 'media' && <MediaLibrary adminToken={adminToken} />}
                {view === 'homepage' && <HomepageCMS adminToken={adminToken} />}
                {view === 'leads' && <LeadsPanel dashboardData={dashboardData} />}
                {view === 'estimates' && (
                  <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <EstimatesManager adminToken={adminToken} />
                  </div>
                )}
                {view === 'analytics' && <AnalyticsDashboard adminToken={adminToken} />}
                {view === 'searches' && <SearchInsightsDashboard adminToken={adminToken} />}
                {view === 'users' && <UsersPanel dashboardData={dashboardData} />}
                {view === 'visit-reports' && <VisitReportsPanel dashboardData={dashboardData} />}
                {view === 'verification' && <VerificationPanel dashboardData={dashboardData} />}
                {view === 'top-venues' && <TopVenuesPanel dashboardData={dashboardData} />}
                {view === 'security' && <SecurityDashboard adminToken={adminToken} />}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Admin Settings</DialogTitle>
            <DialogDescription>Update your admin credentials</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Current Password *</label>
              <Input
                type="password"
                value={settingsForm.currentPassword}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">New Username (optional)</label>
              <Input
                type="text"
                value={settingsForm.newUsername}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, newUsername: e.target.value }))}
                placeholder="Leave blank to keep current"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">New Password (optional)</label>
              <Input
                type="password"
                value={settingsForm.newPassword}
                onChange={(e) => setSettingsForm(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Leave blank to keep current"
              />
            </div>
            <Button onClick={handleUpdateCredentials} className="w-full bg-stone-900 hover:bg-stone-800">
              Update Credentials
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
